import type { ValhallaRouteResult } from "@/api/tripPlanning/types/valhallaTypes";
import type { TripDTO } from "@/api/vtnz-backend/trips";
import type { Vehicle } from "@/domains/vehicles";
import type { VehicleICE } from "@/domains/vehicles/ICE/vehicleICEClass";
import { TripCore, type TripCoreDTO, type TripCoreOptions } from "../core/tripCoreClass";
import {
  TripLocationCore,
  type TripLocationCoreDTO,
} from "../core/tripLocationCoreClass";
import type {
  Trip,
  TripPlanningReturn,
} from "../types";
import { ItineraryICE } from "./itineraryICEClass";

type TripICEOptions = {
  /**
   * Optional for draft trips.
   * An empty itinerary will be created when not provided.
   */
  itinerary?: ItineraryICE;

  vehicle?: VehicleICE;
};

/** Shape stored in a route plan's `planning_parameters` column for an ICE trip. */
type TripICEPlanningParametersDTO = {
  startingLoad: number;
  locations: TripLocationCoreDTO[];
};

export class TripICE extends TripCore {
  // -------------------------------------------------------------------- //
  // ---------------------------- Constructor --------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    externalId,
    status,
    name,
    locations,
    itinerary,
    vehicle,
    startingLoad,
    vehicleExternalId,
  }: Omit<TripCoreOptions, "type" | "itinerary" | "vehicle"> &
    TripICEOptions = {}) {
    super({
      externalId,
      type: "ICE",
      status,
      name,
      locations,
      itinerary: itinerary ?? new ItineraryICE(),
      vehicle,
      startingLoad,
      vehicleExternalId,
    });
  }

  static fromDTO(dto: TripDTO, vehicle: Vehicle | undefined): Trip {
    const planningParameters = dto.planning_parameters as
      | Partial<TripICEPlanningParametersDTO>
      | null;

    return new TripICE({
      externalId: dto.id,
      itinerary: dto.itinerary
        ? ItineraryICE.fromDTO(dto.itinerary)
        : undefined,
      locations: planningParameters?.locations?.map((location) =>
        TripLocationCore.fromDTO(location),
      ),
      name: dto.name ?? undefined,
      startingLoad: Number(planningParameters?.startingLoad ?? 0),
      status: "unplanned",
      vehicle: vehicle,
      vehicleExternalId: dto.vehicle_id,
    });
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Methods ------------------------------ //
  // ----------------------------------------------------------------------- //

  public async changeVehicle(vehicle: Vehicle | null): Promise<void> {
    if (vehicle === null) {
      this.vehicleExternalId = null;
      this.vehicle = null;
      return;
    }

    this.vehicleExternalId = vehicle.externalId;
    this.vehicle = vehicle;

    // re-plan if vehicle is a different costing model
    if (vehicle.valhallaCostingType !== this.itinerary.costingModel) {
      this.planTrip({ forceReCalc: true });
    }
  }

  public async planTrip(
    options: {
      forceReCalc?: boolean;
    } = {},
  ): Promise<TripPlanningReturn> {
    // check if trip has already been planned
    // ASSUMES: if you want to ensure trip is recalculated the forceReCalc property is true.
    if (!options.forceReCalc && this.itinerary.isValid) {
      this.status = this.status === "fallback" ? "fallback" : "success"; // preserve status of previous planning.
      return { success: true, status: "already planned" };
    }

    this.planningFailureReason = null;

    const valhallaPlanningRes = await this.fetchValhallaTripData(
      this.vehicle ?? undefined,
    );

    // ICE vehicles have no fall back strategy, provide an error response if unsuccessful.
    if (!valhallaPlanningRes.success) {
      this.status === "failed";
      this.planningFailureReason = valhallaPlanningRes.error;
      return {
        success: false,
        error: valhallaPlanningRes.error,
      };
    }

    this.itinerary = ItineraryICE.buildFromValhallaTrip({
      costingModel: this.vehicle?.valhallaCostingType ?? "auto",
      locations: this.locations,
      startingLoad: this.startingLoad,
      trip: valhallaPlanningRes.tripData,
    });

    this.status = "success";

    return {
      success: true,
      status: "successfully planned",
    };
  }

  /**
   * ICE trips have no post-optimisation knock-on effects (no energy/charging
   * feasibility to re-check) — just rebuild the itinerary from the reordered
   * stops.
   */
  public async onWaypointsOptimised(
    valhallaRes: ValhallaRouteResult & { success: true },
  ): Promise<TripPlanningReturn> {
    this.itinerary = ItineraryICE.buildFromValhallaTrip({
      costingModel: this.vehicle?.valhallaCostingType ?? "auto",
      locations: this.locations,
      startingLoad: this.startingLoad,
      trip: valhallaRes.route.trip,
    });

    return {
      success: true,
      status: "successfully planned",
    };
  }

  public compileDTO(): TripCoreDTO {
    const planningParameters: TripICEPlanningParametersDTO = {
      startingLoad: this.startingLoad,
      locations: this.locations.map((location) => location.compileDTO()),
    };

    return {
      itinerary: this.itinerary.compileDTO(),
      planning_parameters: planningParameters,
    };
  }
}
